/*
  Glimot header behaviour.
  - <g-header-panel>: Account, Favourites and Cart icons with hover-or-click panels and optional labels.
  - <glimot-menu-drawer>: mobile menu in "full_screen" (accordion) or "side_panel" (sliding) style.
  Loaded deferred by sections/header.liquid, after Craft's global.js.
*/
(() => {
  if (window.customElements.get('g-header-panel')) return;

  const HOVER_OPEN_DELAY = 120;
  const HOVER_CLOSE_DELAY = 260;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const openPanels = new Set();
  let lastPointerType = 'mouse';
  let lastPointerTime = 0;

  document.addEventListener(
    'pointerdown',
    (event) => {
      lastPointerType = event.pointerType || 'mouse';
      lastPointerTime = Date.now();
    },
    true
  );

  const recentTouch = () => (lastPointerType === 'touch' || lastPointerType === 'pen') && Date.now() - lastPointerTime < 800;

  function closeAllPanels(except) {
    openPanels.forEach((panel) => {
      if (panel !== except) panel.close();
    });
  }

  function closeHeaderDisclosures() {
    document.querySelectorAll('header-menu details[open]').forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  function closeMenuDrawer() {
    const drawer = document.querySelector('glimot-menu-drawer');
    if (drawer && drawer.isOpen && drawer.isOpen()) drawer.closeFromOutside();
  }

  window.GlimotHeader = { closeAllPanels };

  class GHeaderPanel extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode === 'click' ? 'click' : 'hover';
      this.name = this.dataset.panel;
      this.trigger = this.querySelector('[data-g-trigger]');
      this.panel = this.querySelector('[data-g-panel]');
      this.label = this.querySelector('[data-g-label]');
      const actions = this.closest('.g-actions');
      this.labelsOn = actions?.dataset.labels === 'true';
      const duration = parseInt(actions?.dataset.labelDuration, 10);
      this.labelDuration = Number.isFinite(duration) && duration >= 0 ? duration : 5;
      if (!this.trigger || !this.panel) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.addEventListener('click', this.onClickCapture.bind(this), true);
      this.addEventListener('pointerenter', this.onPointerEnter.bind(this));
      this.addEventListener('pointerleave', this.onPointerLeave.bind(this));
      this.addEventListener('keydown', this.onKeyDown.bind(this), true);
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.trigger.addEventListener('focus', this.onTriggerFocus.bind(this));
      this.trigger.addEventListener('blur', () => this.hideLabel());
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open({ viaHover = false } = {}) {
      clearTimeout(this.closeTimer);
      if (this.isOpen) return;
      closeAllPanels(this);
      closeHeaderDisclosures();
      closeMenuDrawer();
      this.openedViaHover = viaHover;
      this.panel.hidden = false;
      this.setAttribute('open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
      openPanels.add(this);
      document.addEventListener('click', this.onDocumentClick);
      this.onOpen();
    }

    close({ focusTrigger = false } = {}) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!this.isOpen) return;
      this.panel.hidden = true;
      this.removeAttribute('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      openPanels.delete(this);
      document.removeEventListener('click', this.onDocumentClick);
      if (focusTrigger) this.trigger.focus();
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    onOpen() {
      if (this.name === 'favourites' && window.GlimotFavourites) {
        window.GlimotFavourites.renderPreview(
          this.querySelector('[data-g-favourites-preview]'),
          parseInt(this.dataset.count, 10) || 4
        );
      }
      if (this.name === 'cart') this.refreshCartPreview();
    }

    refreshCartPreview() {
      if (!GHeaderPanel.cartStale) {
        this.trimCartPreview();
        return;
      }
      const container = this.querySelector('[data-g-cart-preview]');
      fetch(`${window.location.pathname}?sections=glimot-cart-preview`)
        .then((response) => response.json())
        .then((sections) => {
          const html = sections['glimot-cart-preview'];
          if (!html || !container) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.g-cart-preview');
          if (fresh) container.replaceChildren(fresh);
          GHeaderPanel.cartStale = false;
          this.trimCartPreview();
          document.dispatchEvent(new CustomEvent('glimot:icons-refresh'));
        })
        .catch(() => {});
    }

    trimCartPreview() {
      const preview = this.querySelector('.g-cart-preview');
      if (!preview) return;
      const limit = parseInt(this.dataset.count, 10) || 3;
      const items = preview.querySelectorAll('.g-cart-preview__item');
      items.forEach((item, index) => (item.hidden = index >= limit));
      const total = parseInt(preview.dataset.totalItems, 10) || items.length;
      let more = preview.querySelector('.g-cart-preview__more');
      if (total > limit) {
        if (!more) {
          more = document.createElement('p');
          more.className = 'g-cart-preview__more';
          preview.querySelector('.g-cart-preview__list')?.after(more);
        }
        more.textContent = (preview.dataset.moreTemplate || '+ [count]').replace('[count]', total - limit);
      } else if (more) {
        more.remove();
      }
    }

    // Main job of the icon when it is not used to toggle the panel.
    runMainAction(event) {
      if (this.name === 'favourites') {
        event.preventDefault();
        this.close();
        document.dispatchEvent(new CustomEvent('glimot:open-favourites', { detail: { opener: this.trigger } }));
        return;
      }
      // Account and Cart are links (Cart may be opened as a drawer by Craft's cart-drawer.js).
      this.close();
    }

    onClickCapture(event) {
      const onTrigger = event.target.closest('[data-g-trigger]') === this.trigger;
      if (event.target.closest('[data-g-open-favourites]')) {
        event.preventDefault();
        this.close();
        document.dispatchEvent(new CustomEvent('glimot:open-favourites', { detail: { opener: this.trigger } }));
        return;
      }
      if (!onTrigger) return;

      const touch = recentTouch();
      if (touch || this.mode === 'click') {
        // Tap (any mode) or click mode: the icon toggles its panel. Stop Craft's cart-drawer
        // listener on the icon from also opening the drawer.
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
        return;
      }
      this.runMainAction(event);
    }

    onPointerEnter(event) {
      if (event.pointerType !== 'mouse' || !canHover.matches) return;
      this.showLabel();
      if (this.mode !== 'hover') return;
      clearTimeout(this.closeTimer);
      this.openTimer = setTimeout(() => this.open({ viaHover: true }), HOVER_OPEN_DELAY);
    }

    onPointerLeave(event) {
      if (event.pointerType !== 'mouse') return;
      this.hideLabel();
      clearTimeout(this.openTimer);
      if (this.mode !== 'hover' || !this.isOpen) return;
      if (this.contains(document.activeElement) && !this.openedViaHover) return;
      this.closeTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY);
    }

    onTriggerFocus() {
      if (recentTouch()) return;
      if (this.trigger.matches(':focus-visible')) this.showLabel();
      // Keyboard equivalent of hover: focusing the icon opens its panel without moving focus.
      if (this.mode === 'hover' && this.trigger.matches(':focus-visible')) this.open();
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyDown(event) {
      if (
        event.key === ' ' &&
        this.mode === 'click' &&
        event.target === this.trigger &&
        this.trigger.tagName === 'A'
      ) {
        // Space toggles a link-based trigger in click mode (buttons handle Space natively).
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
        return;
      }
      if (event.key !== 'Escape') return;
      this.hideLabel();
      if (this.isOpen) {
        event.stopPropagation();
        this.close({ focusTrigger: true });
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    showLabel() {
      if (!this.labelsOn || !this.label) return;
      clearTimeout(this.labelTimer);
      this.label.classList.add('is-visible');
      if (this.labelDuration > 0) {
        this.labelTimer = setTimeout(() => this.hideLabel(), this.labelDuration * 1000);
      }
    }

    hideLabel() {
      if (!this.label) return;
      clearTimeout(this.labelTimer);
      this.label.classList.remove('is-visible');
    }
  }

  GHeaderPanel.cartStale = false;
  customElements.define('g-header-panel', GHeaderPanel);

  // Keep the header in step with the cart: mark the preview stale and close the preview when
  // the side drawer or pop-up takes over.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      GHeaderPanel.cartStale = true;
      document.querySelector('g-header-panel[data-panel="cart"]')?.close();
    });
  }
  document.addEventListener('glimot:cart-changed', () => {
    GHeaderPanel.cartStale = true;
  });

  const drawerObserverTarget = document.querySelector('cart-drawer');
  if (drawerObserverTarget) {
    new MutationObserver(() => {
      if (drawerObserverTarget.classList.contains('active')) closeAllPanels();
    }).observe(drawerObserverTarget, { attributes: true, attributeFilter: ['class'] });
  }
  const notification = document.getElementById('cart-notification');
  if (notification) {
    new MutationObserver(() => {
      if (notification.classList.contains('active')) closeAllPanels();
    }).observe(notification, { attributes: true, attributeFilter: ['class'] });
  }

  // Close icon panels when a mega-menu dropdown opens.
  document.addEventListener(
    'toggle',
    (event) => {
      if (event.target.matches && event.target.matches('header-menu details') && event.target.open) closeAllPanels();
    },
    true
  );

  /* ---------------- Mobile menu ---------------- */
  const HeaderDrawerBase = window.customElements.get('header-drawer');
  if (!HeaderDrawerBase) return;

  class GlimotMenuDrawer extends HeaderDrawerBase {
    // Craft's MenuDrawer calls bindEvents() from its constructor.
    bindEvents() {
      this.menuStyle = this.dataset.style === 'side_panel' ? 'side_panel' : 'full_screen';
      this.allowMultiple = this.dataset.multiple !== 'false';
      this.querySelectorAll('summary').forEach((summary) =>
        summary.addEventListener('click', this.onSummaryClick.bind(this))
      );
      this.querySelectorAll('.menu-drawer__close-button').forEach((button) =>
        button.addEventListener('click', this.onCloseButtonClick.bind(this))
      );
      // Side panel: tapping the dimmed backdrop (a pseudo-element of <details>) closes the menu.
      this.mainDetailsToggle?.addEventListener('click', (event) => {
        if (event.target === this.mainDetailsToggle && this.isOpen()) this.closeFromOutside(event);
      });
      // Whatever closes the menu (our buttons, Craft's global Escape handler on "Details-" elements,
      // focus leaving), always undo the open state so the page is never left inert or locked.
      this.mainDetailsToggle?.addEventListener('toggle', () => {
        if (!this.mainDetailsToggle.hasAttribute('open')) this.cleanupAfterClose();
      });
      this.querySelectorAll('[data-g-menu-close]').forEach((button) =>
        button.addEventListener('click', (event) => this.closeFromOutside(event))
      );
      this.querySelectorAll('[data-g-open-favourites]').forEach((link) =>
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.closeFromOutside();
          document.dispatchEvent(new CustomEvent('glimot:open-favourites', { detail: {} }));
        })
      );
    }

    isOpen() {
      return this.mainDetailsToggle && this.mainDetailsToggle.hasAttribute('open');
    }

    onSummaryClick(event) {
      const summary = event.currentTarget;
      const details = summary.parentNode;
      if (details === this.mainDetailsToggle) {
        if (!details.hasAttribute('open')) closeAllPanels();
        super.onSummaryClick(event);
        // Craft updates aria-expanded at once but changes `open` only after its animation.
        this.updateToggleLabel(summary.getAttribute('aria-expanded') === 'true');
        return;
      }
      if (this.menuStyle === 'side_panel') {
        super.onSummaryClick(event);
        return;
      }
      // Full-screen accordion: let <details> toggle natively, keep aria-expanded in sync.
      const willOpen = !details.hasAttribute('open');
      summary.setAttribute('aria-expanded', String(willOpen));
      if (willOpen && !this.allowMultiple) {
        this.querySelectorAll('details.g-acc[open]').forEach((other) => {
          if (other !== details) {
            other.removeAttribute('open');
            other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          }
        });
      }
    }

    onKeyUp(event) {
      if (event.code?.toUpperCase() !== 'ESCAPE') return;
      if (this.menuStyle === 'full_screen') {
        if (this.isOpen()) this.closeFromOutside(event);
        return;
      }
      super.onKeyUp(event);
    }

    openMenuDrawer(summaryElement) {
      super.openMenuDrawer(summaryElement);
      this.setBackgroundInert(true);
      this.updateToggleLabel(true);
    }

    closeMenuDrawer(event, elementToFocus) {
      if (!elementToFocus) return;
      super.closeMenuDrawer(event, elementToFocus);
      this.querySelectorAll('details.g-acc[open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });
      this.setBackgroundInert(false);
      this.updateToggleLabel(false);
    }

    closeFromOutside(event) {
      const summary = this.mainDetailsToggle.querySelector('summary');
      this.closeMenuDrawer(event || new Event('close'), summary);
      summary.setAttribute('aria-expanded', 'false');
    }

    cleanupAfterClose() {
      const summary = this.mainDetailsToggle.querySelector('summary');
      this.mainDetailsToggle.classList.remove('menu-opening');
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      this.header?.classList.remove('menu-open');
      this.querySelectorAll('details.g-acc[open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });
      summary?.setAttribute('aria-expanded', 'false');
      this.setBackgroundInert(false);
      this.updateToggleLabel(false);
      if (this.contains(document.activeElement) && summary !== document.activeElement) summary?.focus();
    }

    setBackgroundInert(state) {
      document
        .querySelectorAll('#MainContent, .shopify-section-group-footer-group, .shopify-section-group-header-group:not(.section-header)')
        .forEach((element) => {
          if (state) element.setAttribute('inert', '');
          else element.removeAttribute('inert');
        });
    }

    updateToggleLabel(open = this.isOpen()) {
      const summary = this.mainDetailsToggle?.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-label', open ? summary.dataset.labelClose : summary.dataset.labelOpen);
    }
  }

  if (!window.customElements.get('glimot-menu-drawer')) {
    window.customElements.define('glimot-menu-drawer', GlimotMenuDrawer);
  }
})();

/* Mega menu card (design: a 640 px card under its item): keep it inside the viewport. */
document.querySelectorAll('.g-nav details.mega-menu').forEach((details) => {
  details.addEventListener('toggle', () => {
    if (!details.open) return;
    const content = details.querySelector('.mega-menu__content');
    if (!content) return;
    content.style.removeProperty('--g-mega-shift');
    const rect = content.getBoundingClientRect();
    const edge = 16;
    let shift = 0;
    if (rect.left < edge) shift = edge - rect.left;
    else if (rect.right > window.innerWidth - edge) shift = window.innerWidth - edge - rect.right;
    if (shift) content.style.setProperty('--g-mega-shift', `${Math.round(shift)}px`);
  });
});
